import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { DtoCreateApplicationResp } from "@porchpass/api-client";
import type { DtoApplication } from "@porchpass/api-client";
import {
  Alert,
  Button,
  ChevronDownIcon,
  File02Icon,
  FormTable,
  Spinner,
  useApiCall,
} from "@porchpass/ui";
import { CollapsibleContent } from "src/components/CollapsibleContent";
import { iconActionButtonClasses } from "src/components/IconActionButton";
import { useWebClientApi } from "src/api";
import { generic_page_error_message } from "src/constants";
import { useAuth, useUser } from "src/context";
import { DashboardInitializer } from "src/initializers";
import {
  handleCoborrowerInviteRoute,
  handleApplicationProgressRoute,
} from "src/initializers/helpers";
import DashboardLayout from "src/layouts/DashboardLayout";
import {
  getAuthBorrowerInfo,
  getMainBorrowerIdFromApplication,
  getInProgressMainBorrowerApplication,
  getCompletedMainBorrowerApplication,
  logSentryError,
  dealerStorage,
} from "src/utils";
import ApplicationCard from "./ApplicationCard";
import ConfirmDiscardApplicationModal from "./ConfirmDiscardApplicationModal";
import CloneExistingApplicationModal from "./CloneExistingApplicationModal";

const DELETE_ERROR_MESSAGE = `Unable to delete the application already in progress. ${generic_page_error_message}`;
const CLONE_ERROR_MESSAGE = `Unable to create new application from existing one. ${generic_page_error_message}`;
const APPLICATION_ROUTING_ERROR = `Unable to view submitted application. ${generic_page_error_message}`;

export default () => {
  const navigate = useNavigate();
  const { setUserData } = useUser();
  const { user: firebaseUser } = useAuth();
  const api = useWebClientApi();
  const [historyVisible, setHistoryVisible] = useState(false);
  const [isConfirmDiscardModalOpen, setIsConfirmDiscardModalOpen] =
    useState(false);
  const [isCloneExistingModalOpen, setIsCloneExistingModalOpen] =
    useState(false);
  const [shouldSkipCloneEditPrompt, setShouldSkipCloneEditPrompt] =
    useState(false);
  const [pageError, setPageError] = useState("");
  const { dealerName } = useParams();
  const allowDashboardNavigationRef = useRef(false);
  const applicationRoutingParams = {
    setUserData,
    dealerName,
    navigate,
  };

  const handleApplicationDeleteError = (error: Error) => {
    setPageError(DELETE_ERROR_MESSAGE);
    logSentryError(error, "dashboard", "delete-application-error");
  };

  const deleteApplicationApi = useApiCall(
    (applicationId: string) => api.deleteApplication(applicationId),
    {
      toast: { success: false, error: false },
      onSuccess: () => startNewApplication(allowDashboardNavigationRef.current),
      onError: handleApplicationDeleteError,
    },
  );

  const handleApplicationCloneSuccess = (data: DtoCreateApplicationResp) => {
    const { application_id, borrower_id } = data;

    setUserData?.((prev) => ({
      ...prev,
      application_id,
      borrower_id,
      is_primary: true,
      status: "IN_PROGRESS",
      auth_user_borrower_id: borrower_id,
    }));
    console.log(
      `Redirecting to cloned application ${application_id} for ${dealerName}`,
    );
    navigate(`/${dealerName}/application/${application_id}/progress`);
  };

  const handleApplicationCloneError = (error: Error) => {
    setPageError(CLONE_ERROR_MESSAGE);
    logSentryError(error, "dashboard", "clone-application-error");
  };

  const cloneApplicationApi = useApiCall(
    () => api.copyApplication({ dealer_id: dealerName ?? "" }),
    {
      toast: { success: false, error: false },
      onSuccess: handleApplicationCloneSuccess,
      onError: handleApplicationCloneError,
    },
  );

  const handleApplicationSelect = (application: DtoApplication) => {
    const authBorrower = getAuthBorrowerInfo(application, firebaseUser);
    const { status, is_primary } = authBorrower;

    if (!is_primary && status === "INVITED") {
      return selectInvitedApplication(application);
    }

    selectEditableApplication(application);
  };

  const selectInvitedApplication = (application: DtoApplication) => {
    const authBorrower = getAuthBorrowerInfo(application, firebaseUser);
    const mainBorrower = getMainBorrowerIdFromApplication(application);

    handleCoborrowerInviteRoute(
      application,
      authBorrower,
      mainBorrower,
      applicationRoutingParams,
      true,
    );
  };

  const selectEditableApplication = (application: DtoApplication) => {
    const authBorrower = getAuthBorrowerInfo(application, firebaseUser);
    const mainBorrower = getMainBorrowerIdFromApplication(application);

    handleApplicationProgressRoute(
      application,
      authBorrower,
      mainBorrower,
      applicationRoutingParams,
    );
  };

  const handleSubmittedApplicationSelect = (
    applications: DtoApplication[] | null,
  ) => {
    const submittedApplication = getCompletedMainBorrowerApplication(
      applications,
      firebaseUser,
    );
    if (!submittedApplication) {
      return setPageError(APPLICATION_ROUTING_ERROR);
    }
    handleApplicationSelect(submittedApplication);
  };

  const handleHistoryDisplayToggle = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setHistoryVisible(!historyVisible);
  };

  const startNewApplication = (isReturning = true) => {
    setUserData?.({
      dealer_id: dealerName,
    });
    if (firebaseUser?.email) {
      dealerStorage.setItem("email-for-register", firebaseUser.email);
    }
    if (firebaseUser?.phoneNumber) {
      dealerStorage.setItem("phone-for-register", firebaseUser.phoneNumber);
    }
    navigate(
      `/${dealerName}/register${isReturning ? "?isReturningUser=true" : ""}`,
    );
  };

  const handleApplicationAdd = (
    applications: DtoApplication[] | null,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    const mustConfirmDelete = !!getInProgressMainBorrowerApplication(
      applications,
      firebaseUser,
    );
    const completedApplication = getCompletedMainBorrowerApplication(
      applications,
      firebaseUser,
    );

    if (mustConfirmDelete) {
      setIsConfirmDiscardModalOpen(true);
      return;
    }
    if (completedApplication) {
      setShouldSkipCloneEditPrompt(completedApplication.status === "REVIEWED");
      setIsCloneExistingModalOpen(true);
      return;
    }
    startNewApplication();
  };

  const handleApplicationDiscardAndStartNew = async (
    applications: DtoApplication[] | null,
  ) => {
    setPageError("");
    const deletedApplication = getInProgressMainBorrowerApplication(
      applications,
      firebaseUser,
    );
    allowDashboardNavigationRef.current =
      applications?.filter((app) => {
        const authBorrower = getAuthBorrowerInfo(app, firebaseUser);
        return authBorrower.status !== "INVITED";
      }).length !== 1;

    if (deletedApplication?.id) {
      await deleteApplicationApi.execute(deletedApplication.id);
    }
    setIsConfirmDiscardModalOpen(false);
  };

  const handleApplicationClone = async () => {
    setPageError("");
    await cloneApplicationApi.execute(undefined);
    setIsCloneExistingModalOpen(false);
  };

  const handleCloseCloneModal = () => {
    setIsCloneExistingModalOpen(false);
    setShouldSkipCloneEditPrompt(false);
  };

  const activeApplicationList = (applications: DtoApplication[] | null) => {
    if (!applications || applications.length === 0) return [];

    const mostRecentApplication = applications[0];
    const filteredApplications = applications
      .slice(1)
      .filter(
        (application) =>
          application.status !== "SUBMITTED" &&
          application.status !== "REVIEWED",
      );
    return [mostRecentApplication, ...filteredApplications];
  };

  const pendingApplicationList = (applications: DtoApplication[] | null) => {
    if (!applications || applications.length === 0) return [];

    return applications
      .slice(1)
      .filter(
        (application) =>
          application.status === "SUBMITTED" ||
          application.status === "REVIEWED",
      );
  };

  const renderDashboardPageContent = (
    applications: DtoApplication[] | null,
    isLoading: boolean,
  ) => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-4 p-5">
          <Spinner size="l" />
        </div>
      );
    }

    const activeApplications = activeApplicationList(applications);
    const pendingApplications = pendingApplicationList(applications);

    return (
      <>
        <div className="mb-20 flex h-full w-full min-h-[calc(100vh-250px)] flex-col items-stretch gap-[10px] p-8">
          <h2 className="mb-5 text-center text-2xl font-semibold">Dashboard</h2>
          {pageError ? (
            <Alert variant="error">{pageError}</Alert>
          ) : (
            <Alert variant="info">
              Click to open any of the applications from the list below.
            </Alert>
          )}
          {activeApplications.length > 0 && (
            <FormTable
              title="Your Applications"
              headerIcon={
                <File02Icon
                  variant="bulk-rounded"
                  size={16}
                  className="text-[#6F41E6]"
                />
              }
            >
              {activeApplications.map((application, index) => (
                <ApplicationCard
                  key={application.id}
                  index={index}
                  application={application}
                  selectApplication={handleApplicationSelect}
                  authUser={firebaseUser}
                />
              ))}
            </FormTable>
          )}
          {pendingApplications && pendingApplications.length > 0 && (
            <div className="flex w-full flex-col gap-2.5 py-3">
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between rounded-[10px] border-0 bg-transparent p-4 focus:outline focus:outline-[3px] focus:outline-[Highlight] focus:outline-offset-[3px] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[Highlight] focus-visible:outline-offset-[3px]"
                onClick={handleHistoryDisplayToggle}
                tabIndex={0}
                aria-expanded={historyVisible}
              >
                <h2 className="text-[#59595b]">Previous Applications</h2>
                <span className={iconActionButtonClasses} aria-hidden>
                  <ChevronDownIcon
                    variant="stroke-sharp"
                    size={14}
                    className={`transition-transform duration-300 ease-out ${
                      historyVisible ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </button>
              <CollapsibleContent isOpen={historyVisible}>
                {pendingApplications.map((application, index) => (
                  <ApplicationCard
                    key={application.id}
                    index={index}
                    application={application}
                    selectApplication={handleApplicationSelect}
                    authUser={firebaseUser}
                  />
                ))}
              </CollapsibleContent>
            </div>
          )}
          <Button
            type="button"
            variant="primary"
            size="l"
            className="fixed bottom-8 left-1/2 w-full max-w-[246px] -translate-x-1/2"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
              handleApplicationAdd(applications, event)
            }
          >
            + New Application
          </Button>
        </div>
        <ConfirmDiscardApplicationModal
          isOpen={isConfirmDiscardModalOpen}
          closeHandler={() => setIsConfirmDiscardModalOpen(false)}
          confirmHandler={() =>
            void handleApplicationDiscardAndStartNew(applications)
          }
        />
        <CloneExistingApplicationModal
          isOpen={isCloneExistingModalOpen}
          shouldSkipEditPrompt={shouldSkipCloneEditPrompt}
          closeHandler={handleCloseCloneModal}
          confirmEditApp={() => handleSubmittedApplicationSelect(applications)}
          confirmCloneApp={handleApplicationClone}
          confirmStartNewApp={startNewApplication}
        />
      </>
    );
  };

  return (
    <DashboardInitializer>
      {({ applications, isLoading, error }) => (
        <DashboardLayout pageTitle="Dashboard">
          {error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            renderDashboardPageContent(applications, isLoading)
          )}
        </DashboardLayout>
      )}
    </DashboardInitializer>
  );
};
